import type { FC, ReactNode } from 'react';

import type { CheckboxProps } from '@mui/material';
import { Typography } from '@mui/material';
import { Checkbox } from '@truvhq/ui';
import { clsx } from 'clsx';
import { type RegisterOptions, useController, useFormContext } from 'react-hook-form';

type CheckboxFormFieldProps = Omit<CheckboxProps, 'name' | 'checked' | 'defaultChecked' | 'inputRef'> & {
  name: string;
  label: ReactNode;
  description?: ReactNode;
  rules?: RegisterOptions;
  invertValue?: boolean;
  className?: string;
  'data-testid'?: string;
};

export const CheckboxFormField: FC<CheckboxFormFieldProps> = ({
  name,
  label,
  description,
  rules,
  invertValue = false,
  className,
  onChange,
  onBlur,
  'data-testid': dataTestId,
  ...checkboxProps
}) => {
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ control, name, rules });

  return (
    <div className={clsx('flex w-full items-center', className)}>
      <Checkbox
        color="primary"
        {...checkboxProps}
        checked={invertValue ? !field.value : !!field.value}
        className="-ml-2"
        data-testid={dataTestId ?? name}
        id={name}
        inputRef={field.ref}
        name={field.name}
        onBlur={(event) => {
          field.onBlur();
          onBlur?.(event);
        }}
        onChange={(event, checked) => {
          field.onChange(invertValue ? !checked : checked);
          onChange?.(event, checked);
        }}
      />
      <div className="flex flex-1 flex-col">
        <Typography className="text-sm leading-[18px]" component="label" htmlFor={name}>
          {label}
        </Typography>
        {description && (
          <Typography className="text-grey60 leading-[18px]" variant="caption">
            {description}
          </Typography>
        )}
        {error?.message && (
          <Typography className="text-red-main" variant="caption">
            {error.message}
          </Typography>
        )}
      </div>
    </div>
  );
};
